<template>
    <div class="text-editor-container">
        <label class="form-label" v-if="label">{{ label }}</label>
        
        <div class="email-toolbar">
            <div class="toolbar-group">
                <button class="toolbar-btn" @click.prevent="formatText('bold')" title="Negrito">
                    <i class="fas fa-bold"></i>
                </button>

                <button class="toolbar-btn" @click.prevent="formatText('italic')" title="Itálico">
                    <i class="fas fa-italic"></i>
                </button>

                <button class="toolbar-btn" @click.prevent="formatText('underline')" title="Sublinhado">
                    <i class="fas fa-underline"></i>
                </button>

                <button class="toolbar-btn" @click.prevent="formatText('strikeThrough')" title="Riscado">
                    <i class="fas fa-strikethrough"></i>
                </button>
            </div>

            <div class="toolbar-separator"></div>

            <div class="toolbar-group">
                <select class="toolbar-select" v-model="textFont">
                    <option value="">Fonte</option>
                    <option value="Arial">Arial</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Courier New">Courier New</option>
                    <option value="Verdana">Verdana</option>
                </select>

                <select class="toolbar-select" v-model="textSize">
                    <option value="">Tamanho</option>
                    <option value="1">8pt</option>
                    <option value="2">10pt</option>
                    <option value="3">12pt</option>
                    <option value="4">14pt</option>
                    <option value="5">16pt</option>
                    <option value="6">18pt</option>
                    <option value="7">24pt</option>
                </select>
            </div>

            <div class="toolbar-separator"></div>

            <div class="toolbar-group">
                <input type="color" class="color-picker" v-model="textColor" title="Cor do texto">
                <input type="color" class="color-picker" v-model="textBackground" title="Cor de fundo" value="#ffffff">
            </div>

            <div class="toolbar-separator"></div>

            <div class="toolbar-group">
                <button class="toolbar-btn" @click.prevent="formatText('justifyLeft')" title="Alinhar à esquerda">
                    <i class="fas fa-align-left"></i>
                </button>

                <button class="toolbar-btn" @click.prevent="formatText('justifyCenter')" title="Centralizar">
                    <i class="fas fa-align-center"></i>
                </button>

                <button class="toolbar-btn" @click.prevent="formatText('justifyRight')" title="Alinhar à direita">
                    <i class="fas fa-align-right"></i>
                </button>

                <button class="toolbar-btn" @click.prevent="formatText('justifyFull')" title="Justificar">
                    <i class="fas fa-align-justify"></i>
                </button>
            </div>

            <div class="toolbar-separator"></div>

            <div class="toolbar-group">
                <button class="toolbar-btn" @mousedown.prevent="formatText('insertUnorderedList')" title="Lista com marcadores">
                    <i class="fas fa-list-ul"></i>
                </button>

                <button class="toolbar-btn" @mousedown.prevent="formatText('insertOrderedList')" title="Lista numerada">
                    <i class="fas fa-list-ol"></i>
                </button>

                <button class="toolbar-btn" @mousedown.prevent="formatText('indent')" title="Aumentar recuo">
                    <i class="fas fa-indent"></i>
                </button>

                <button class="toolbar-btn" @mousedown.prevent="formatText('outdent')" title="Diminuir recuo">
                    <i class="fas fa-outdent"></i>
                </button>
            </div>

            <div class="toolbar-separator"></div>

            <div class="toolbar-group">
                <button class="toolbar-btn" @click.prevent="insertLink()" title="Inserir link">
                    <i class="fas fa-link"></i>
                </button>
                <button class="toolbar-btn" @click.prevent="insertImage()" title="Inserir imagem">
                    <i class="fas fa-image"></i>
                </button>
            </div>

            <div class="toolbar-separator"></div>

            <div class="toolbar-group">
                <button class="toolbar-btn" @click.prevent="formatText('removeFormat')" title="Remover formatação">
                    <i class="fas fa-trash-alt"></i>
                </button>
            </div>
        </div>

        <div class="email-editor" 
             contenteditable="true" 
             :id="editorId"
             @input="updateContent"
             @focus="onFocus"
             @blur="onBlur">
        </div>
    </div>
</template>

<script>
export default {
    name: "TextEditor",
    props: {
        value: {
            type: String,
            default: ""
        },
        label: {
            type: String,
            default: ""
        },
        editorId: {
            type: String,
            default: () => `editor-${Math.random().toString(36).substr(2, 9)}`
        },
        placeholder: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            textSize: "",
            textFont: "",
            textColor: "",
            textBackground: "",
        }
    },
    mounted() {
        this.setContent(this.value);
        if (this.placeholder) {
            this.setPlaceholder();
        }
    },
    watch: {
        textSize(value) {
            if (!value) return;
            this.formatText("fontSize", value);
        },
        textFont(value) {
            if (!value) return;
            this.formatText("fontName", value);
        },
        textColor(value) {
            if (!value) return;
            this.formatText("foreColor", value);
        },
        textBackground(value) {
            if (!value) return;
            this.formatText("backColor", value);
        },
        value(newValue) {
            if (newValue !== this.getContent()) {
                this.setContent(newValue);
            }
        }
    },
    methods: {
        formatText(command, value = null) {
            document.execCommand(command, false, value);
            this.focusEditor();
        },
        insertLink() {
            const url = prompt('Digite a URL:');
            if (url) {
                document.execCommand('createLink', false, url);
            }
            this.focusEditor();
        },
        insertImage() {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = (e) => {
                const file = e.target.files[0];
                if (file) {
                    this.processImageFile(file);
                }
            };
            input.click();
        },
        processImageFile(file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64Data = e.target.result;
                this.insertImageIntoEditor(base64Data, file.name);
            };
            reader.readAsDataURL(file);
        },
        insertImageIntoEditor(base64Data, fileName) {
            const editor = document.getElementById(this.editorId);
            if (editor) {
                const imgElement = `<img src="${base64Data}" alt="${fileName}" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
                
                const selection = window.getSelection();
                if (selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
                    const range = selection.getRangeAt(0);
                    range.deleteContents();
                    range.insertNode(range.createContextualFragment(imgElement));
                    range.collapse(false);
                } else {
                    editor.innerHTML += imgElement;
                }
                
                this.updateContent();
                this.focusEditor();
            }
        },
        updateContent() {
            const content = this.getContent();
            this.$emit('input', content);
            this.$emit('change', content);
        },
        getContent() {
            const editor = document.getElementById(this.editorId);
            return editor ? editor.innerHTML : '';
        },
        setContent(content) {
            const editor = document.getElementById(this.editorId);
            if (editor) {
                editor.innerHTML = content || '';
            }
        },
        clearContent() {
            this.setContent('');
            this.$emit('input', '');
            this.$emit('change', '');
        },
        focusEditor() {
            const editor = document.getElementById(this.editorId);
            if (editor) {
                editor.focus();
            }
        },
        setPlaceholder() {
            const editor = document.getElementById(this.editorId);
            if (editor && this.placeholder) {
                if (!editor.innerHTML || editor.innerHTML === '<br>') {
                    editor.setAttribute('data-placeholder', this.placeholder);
                }
            }
        },
        onFocus() {
            this.$emit('focus');
        },
        onBlur() {
            this.$emit('blur');
        }
    }
}
</script>

<style scoped>
.text-editor-container {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.form-label {
    font-size: 11px;
    font-weight: bold;
    color: #333;
}

.email-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    align-items: center;
    padding: 10px;
    background: #f8f9fa;
    border: 1px solid #c9c9c9;
    border-bottom: none;
    border-radius: 4px 4px 0 0 !important;
}

.toolbar-group {
    display: flex;
    gap: 2px;
    align-items: center;
}

.toolbar-separator {
    width: 1px;
    height: 20px;
    background: #c9c9c9;
    margin: 0 5px;
}

.toolbar-btn {
    padding: 6px 8px;
    border: 1px solid #c9c9c9;
    background: white;
    border-radius: 2px !important;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
    min-width: 28px;
    text-align: center;
}

.toolbar-btn:hover {
    background-color: #e9ecef;
    border-color: #000000;
}

.toolbar-btn .active {
    background-color: #2e8b8b;
    color: white;
    border-color: #2e8b8b;
}

.toolbar-select {
    padding: 4px 6px;
    border: 1px solid #c9c9c9;
    border-radius: 3px !important;
    font-size: 11px;
    background: white;
    cursor: pointer;
}

.toolbar-select:focus {
    outline: none;
    border-color: #2e8b8b;
}

.color-picker {
    width: 28px;
    height: 28px;
    border: 1px solid #c9c9c9;
    border-radius: 3px !important;
    cursor: pointer;
    padding: 0;
}

.email-editor {
    min-height: 200px;
    max-height: 400px;
    border: 1px solid #c9c9c9;
    border-radius: 0 0 4px 4px !important;
    padding: 15px;
    font-size: 13px;
    line-height: 1.6;
    overflow-y: auto;
    background: white;
    outline: none;
}

.email-editor:focus {
    border-color: #2e8b8b;
    box-shadow: 0 0 3px rgba(46, 139, 139, 0.2);
}

.email-editor p {
    margin: 0 0 10px 0;
}

.email-editor ul,
.email-editor ol {
    margin: 10px 0;
    padding-left: 30px;
}

.email-editor table {
    border-collapse: collapse;
    width: 100%;
    margin: 10px 0;
}

.email-editor table td,
.email-editor table th {
    border: 1px solid #c9c9c9;
    padding: 8px;
    text-align: left;
}

.email-editor table th {
    background-color: #f8f9fa;
    font-weight: bold;
}

.email-editor[data-placeholder]:empty::before {
    content: attr(data-placeholder);
    color: #999;
    pointer-events: none;
    position: absolute;
}
</style>